import { displayName, type Profiles } from './people';
import { visibleComments } from './comments';

/**
 * Telling someone something happened, without a backend.
 *
 * Real email needs a server to send it, which on this project needs billing
 * enabled. What works today is the browser's own notifications: while the board
 * is open in a tab — even a background one — a comment or a mention raises a
 * desktop notification. That covers the person who leaves it open, which is the
 * behaviour we actually saw, and costs nothing.
 *
 * It does NOT reach someone whose tab is closed. That is a real limit, said out
 * loud in the settings rather than discovered later.
 */
export type NotifyWhen = 'all' | 'mentions' | 'none';

export const NOTIFY_LABEL: Record<NotifyWhen, string> = {
  all: 'Every comment',
  mentions: 'Only when I am mentioned',
  none: 'Never',
};

export const NOTIFY_BLURB: Record<NotifyWhen, string> = {
  all: 'A notification for every comment anyone writes.',
  mentions: 'Only when someone types @ and your name.',
  none: 'Nothing. The unread count on Activity still works.',
};

function canNotify(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function permission(): NotificationPermission | 'unsupported' {
  return canNotify() ? Notification.permission : 'unsupported';
}

export async function askPermission(): Promise<NotificationPermission | 'unsupported'> {
  if (!canNotify()) return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission;
  return Notification.requestPermission();
}

export interface Incoming {
  author: string;
  text: string;
  issueRef: string;
  issueTitle: string;
  issueId: string;
  mentionsMe: boolean;
}

/** Decides whether this one is worth interrupting someone for. */
export function shouldNotify(n: Incoming, when: NotifyWhen): boolean {
  if (when === 'none') return false;
  if (when === 'mentions') return n.mentionsMe;
  return true;
}

export function show(n: Incoming, profiles: Profiles, onOpen: (issueId: string) => void): void {
  if (permission() !== 'granted') return;
  const who = displayName(n.author, profiles);
  const note = new Notification(`${who} on ${n.issueRef}`, {
    body: n.text.length > 140 ? `${n.text.slice(0, 137)}…` : n.text,
    // Same tag per issue, so a busy thread replaces rather than stacks.
    tag: `flightdeck-${n.issueId}`,
    icon: '/favicon.svg',
  });
  note.onclick = () => {
    window.focus();
    onOpen(n.issueId);
    note.close();
  };
}

/**
 * Which comments are worth telling someone about, given what has already been
 * accounted for.
 *
 * Kept out of the database subscription so it can be tested. The property that
 * matters is the first one: on the very first snapshot every comment ever
 * written is "new", and firing for all of them is how someone learns to switch
 * notifications off within a day.
 */
export function collectNew(
  issues: {
    id: string;
    ref: string;
    title: string;
    comments?: { id: string; author: string; text: string; removed?: boolean }[];
  }[],
  seen: Set<string>,
  me: string,
  primed: boolean,
  mentionsMe: (text: string, me: string) => boolean
): Incoming[] {
  const out: Incoming[] = [];
  for (const issue of issues) {
    for (const c of visibleComments(issue.comments)) {
      if (seen.has(c.id)) continue;
      seen.add(c.id);
      if (!primed) continue; // first pass only learns what already exists
      if (c.author.toLowerCase() === me.toLowerCase()) continue; // never your own
      if (!c.author.includes('@')) continue; // written by a system, not a person
      out.push({
        author: c.author,
        text: c.text,
        issueRef: issue.ref,
        issueTitle: issue.title,
        issueId: issue.id,
        mentionsMe: mentionsMe(c.text, me),
      });
    }
  }
  return out;
}
